import React, { memo, useState } from 'react'
import { Button } from 'transfix-ui/components/Button'
import { Card } from 'transfix-ui/components/Card'
import { ExpandableRow } from 'transfix-ui/components/ExpandableRow'
import { ConfirmationModal } from 'transfix-ui/components/Modals'
import { Box, makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { getCostStrategy } from 'apps/marketplace/pages/spot-quote-tool/utils/getCostStrategy'
import { ISpotRate } from 'apps/marketplace/store/amQuoting/types'
import {
  COST_STRATEGY,
  STRATEGY_CHOSEN,
} from 'apps/spot-quote-control-panel/types/spotBiddingConfig'
import { InfoIcon } from 'transfix-icons'
import { Tooltip } from 'transfix-ui'
import { SpotRulesTable } from './SpotRulesTable'

interface ISpotRulesCardProps {
  spotRate: Partial<ISpotRate> & { metadata: ISpotRate['metadata'] }
  error?: string
  modalClassName?: string
}

const useStyles = makeStyles(theme => ({
  modal: {
    borderBottom: `1px solid  ${brandColors.coolGray4}`,
    borderTop: `1px solid  ${brandColors.coolGray4}`,
  },
  row: {
    padding: `${theme.spacing(0.75)} !important`,
    boxShadow: `none !important`,
    '&:hover': {
      backgroundColor: `${brandColors.white} !important`,
    },
  },
  details: {
    backgroundColor: `${brandColors.white} !important`,
    border: `none !important`,
    padding: `${theme.spacing(0.25)} ${theme.spacing(0.5)} !important`,
  },
}))

export const SpotRulesCard = memo<ISpotRulesCardProps>(({ modalClassName, spotRate }) => {
  const classes = useStyles()
  const {
    metadata: {
      appliedCostControlConfig,
      customRules,
      dsCostPremiumPct,
      finalManualCostAdjustmentPercent,
      isDoNotBid,
      spotBiddingRules,
      strategyChosen,
      strikePriceRules,
    },
  } = spotRate

  const appliedCustomRules = customRules || spotBiddingRules?.appliedCustomRules || []

  const [showModal, modalToggle] = useState<boolean>(false)

  const modalHandler = () => modalToggle(!showModal)

  const costStrategy = getCostStrategy(strategyChosen, dsCostPremiumPct)

  const adjustmentPct =
    typeof finalManualCostAdjustmentPercent === 'number'
      ? finalManualCostAdjustmentPercent.toFixed(2)
      : 'N/A'

  return (
    <div>
      <div className='pb3'>
        <Typography variant='subtitle2'>Spot Rules</Typography>
      </div>
      <Card>
        <ExpandableRow
          rowClass={classes.row}
          row={
            <>
              <div className='b'>Spot Quote Rules</div>
              {!isDoNotBid && (
                <Box
                  className='flex flex-row items-center'
                  style={{ color: brandColors.coolGray5 }}>
                  {costStrategy}
                  {strategyChosen === STRATEGY_CHOSEN.strikePrice && (
                    <>
                      {' '}
                      · {adjustmentPct}% Total SPCP Cost Adjustment &nbsp;
                      <Tooltip position='top' effect='solid' trigger={<InfoIcon size='medium' />}>
                        This sums the impact from applied spot quote rules.
                      </Tooltip>
                    </>
                  )}
                  {costStrategy === COST_STRATEGY.bidAtCost && (
                    <>
                      {' '}
                      · {adjustmentPct}% Total Cost Adjustment &nbsp;
                      <Tooltip position='top' effect='solid' trigger={<InfoIcon size='medium' />}>
                        This sums the impact from applied spot quote rules.
                      </Tooltip>
                    </>
                  )}
                </Box>
              )}
            </>
          }
          detailsClass={classes.details}
          details={
            <>
              <SpotRulesTable
                costControlConfig={[appliedCostControlConfig]}
                strikePriceRules={strikePriceRules?.appliedRules || []}
                spotBiddingRules={spotBiddingRules?.appliedRules || []}
                customRules={appliedCustomRules}
                isDoNotBid={isDoNotBid}
                strategyChosen={strategyChosen}
              />
              <Box textAlign='right'>
                <Button variant='text' color='tertiary' onClick={modalHandler}>
                  Raw Spot Bidding Rules
                </Button>
              </Box>
              {showModal && (
                <ConfirmationModal
                  title='Raw Spot Bidding Rules'
                  open={showModal}
                  className={modalClassName}
                  onClose={modalHandler}
                  actions={[
                    {
                      label: 'Close',
                      type: 'dismiss',
                      ButtonProps: {
                        size: 'small',
                        color: 'primary',
                      },
                    },
                  ]}>
                  <div className={classes.modal}>
                    <pre>
                      <code>{JSON.stringify(spotBiddingRules, undefined, 2)}</code>
                    </pre>
                  </div>
                </ConfirmationModal>
              )}
            </>
          }
        />
      </Card>
    </div>
  )
})
